import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { XMarkIcon } from '@heroicons/react/24/outline';
import type {
  NIBEmployeeRecord,
  NIBFormatType,
  NIBContributionPreview,
  NIBPayIndicator,
} from '@/types';
import { NIBCategory, NIB_CATEGORY_INFO, NIB_CONSTANTS } from '@/types';
import { calculateNIBContributions } from '@/services/utils/nibHelpers';
import { ContributionPreview } from './ContributionPreview';
import { useDebounce } from '@hooks/useDebounce';

interface RecordFormProps {
  onSubmit: (data: RecordFormData) => void;
  onCancel: () => void;
  initialData?: NIBEmployeeRecord;
  isLoading?: boolean;
  formatType: NIBFormatType;
}

interface RecordFormData {
  employee_ni_number: string;
  last_name: string;
  first_name: string;
  middle_initial?: string;
  category: NIBCategory; // 1-7 (July 2024 category system)
  weeks_worked: number;
  original_gross_wages: number;
  original_gratuity?: number;
}

export function RecordForm({
  onSubmit,
  onCancel,
  initialData,
  isLoading = false,
  formatType,
}: RecordFormProps) {
  const [contributionPreview, setContributionPreview] =
    useState<NIBContributionPreview | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<RecordFormData>({
    defaultValues: initialData
      ? {
          employee_ni_number: initialData.employee_ni_number,
          last_name: initialData.last_name,
          first_name: initialData.first_name,
          middle_initial: initialData.middle_initial || '',
          category: initialData.category,
          weeks_worked: initialData.weeks_worked,
          original_gross_wages: initialData.original_gross_wages,
          original_gratuity: initialData.original_gratuity || 0,
        }
      : {
          category: NIBCategory.REGULAR,
          weeks_worked: 4,
          original_gross_wages: 0,
          original_gratuity: 0,
        },
  });

  // React Hook Form's watch() returns new instances, cannot be memoized
  const watchedFields = watch();
  const debouncedWages = useDebounce(watchedFields.original_gross_wages, 500);
  const debouncedGratuity = useDebounce(watchedFields.original_gratuity, 500);
  const debouncedWeeks = useDebounce(watchedFields.weeks_worked, 500);
  const category = watch('category');

  useEffect(() => {
    const calculatePreview = () => {
      const wages = debouncedWages || 0;
      const weeks = debouncedWeeks || 0;
      const gratuity = debouncedGratuity || 0;

      if (wages <= 0 || weeks <= 0 || weeks > 5) {
        setContributionPreview(null);
        return;
      }

      // Use the NIB helper function for accurate calculation
      const result = calculateNIBContributions({
        grossWage: wages,
        gratuityWages: formatType === 'HOSPITALITY' ? gratuity : undefined,
        category: category,
        payIndicator: 'W' as NIBPayIndicator, // Weekly
        weeksWorked: weeks,
      });

      // Get category info for rates
      const categoryInfo = NIB_CATEGORY_INFO[category];

      setContributionPreview({
        gross_wages: wages,
        insurable_wages: result.insurableWages,
        employee_contribution: result.employeeContribution,
        employer_contribution: result.employerContribution,
        gratuity: formatType === 'HOSPITALITY' ? gratuity : undefined,
        gratuity_contribution: result.gratuityContribution,
        total_contribution: result.totalContribution,
        wage_was_truncated: result.wasTruncated,
        wage_was_capped: result.wasCapped,
        calculation_details: {
          employee_rate: categoryInfo.employeeRate,
          employer_rate: categoryInfo.employerRate,
          gratuity_rate: 0.098, // 9.8%
          weekly_cap: categoryInfo.weeklyCap,
          monthly_cap: categoryInfo.monthlyCap,
        },
      });
    };

    calculatePreview();
  }, [debouncedWages, debouncedGratuity, debouncedWeeks, category, formatType]);

  const categoryOptions = Object.entries(NIB_CATEGORY_INFO).map(
    ([catNum, info]) => ({
      label: info.name,
      value: parseInt(catNum) as NIBCategory,
    })
  );

  return (
    <div className="bg-white rounded-lg shadow-lg">
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">
          {initialData ? 'Edit Employee Record' : 'Add Employee Record'}
        </h3>
        <button
          type="button"
          onClick={onCancel}
          className="text-gray-400 hover:text-gray-500"
        >
          <XMarkIcon className="h-6 w-6" />
        </button>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="px-6 py-6">
        <div className="space-y-6">
          {/* NI Number */}
          <div>
            <label
              htmlFor="employee_ni_number"
              className="block text-sm font-medium text-gray-700"
            >
              National Insurance Number <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="employee_ni_number"
              {...register('employee_ni_number', {
                required: 'NI Number is required',
                pattern: {
                  value: /^\d{8}$/,
                  message: 'NI Number must be exactly 8 digits',
                },
              })}
              maxLength={8}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm font-mono"
              placeholder="12345678"
            />
            {errors.employee_ni_number && (
              <p className="mt-1 text-sm text-red-600">
                {errors.employee_ni_number.message}
              </p>
            )}
          </div>

          {/* Name Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="first_name"
                className="block text-sm font-medium text-gray-700"
              >
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="first_name"
                {...register('first_name', {
                  required: 'First name is required',
                  maxLength: {
                    value: 20,
                    message: 'First name must be 20 characters or less',
                  },
                })}
                maxLength={20}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm uppercase"
                placeholder="JOHN"
              />
              {errors.first_name && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.first_name.message}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="last_name"
                className="block text-sm font-medium text-gray-700"
              >
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="last_name"
                {...register('last_name', {
                  required: 'Last name is required',
                  maxLength: {
                    value: 20,
                    message: 'Last name must be 20 characters or less',
                  },
                })}
                maxLength={20}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm uppercase"
                placeholder="SMITH"
              />
              {errors.last_name && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.last_name.message}
                </p>
              )}
            </div>
          </div>

          {/* Middle Initial */}
          <div>
            <label
              htmlFor="middle_initial"
              className="block text-sm font-medium text-gray-700"
            >
              Middle Initial (Optional)
            </label>
            <input
              type="text"
              id="middle_initial"
              {...register('middle_initial', {
                maxLength: {
                  value: 1,
                  message: 'Middle initial must be 1 character',
                },
              })}
              maxLength={1}
              className="mt-1 block w-full md:w-20 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm uppercase"
              placeholder="M"
            />
            {errors.middle_initial && (
              <p className="mt-1 text-sm text-red-600">
                {errors.middle_initial.message}
              </p>
            )}
          </div>

          {/* Employee Type and Weeks */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="category"
                className="block text-sm font-medium text-gray-700"
              >
                Employee Category <span className="text-red-500">*</span>
              </label>
              <select
                id="category"
                {...register('category', {
                  required: 'Employee category is required',
                  valueAsNumber: true,
                })}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                {categoryOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.category && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.category.message}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="weeks_worked"
                className="block text-sm font-medium text-gray-700"
              >
                Weeks Worked <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                id="weeks_worked"
                {...register('weeks_worked', {
                  required: 'Weeks worked is required',
                  min: {
                    value: 1,
                    message: 'Must be at least 1 week',
                  },
                  max: {
                    value: NIB_CONSTANTS.MAX_WEEKS_PER_MONTH,
                    message: `Maximum ${NIB_CONSTANTS.MAX_WEEKS_PER_MONTH} weeks`,
                  },
                  valueAsNumber: true,
                })}
                min={1}
                max={NIB_CONSTANTS.MAX_WEEKS_PER_MONTH}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
              {errors.weeks_worked && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.weeks_worked.message}
                </p>
              )}
            </div>
          </div>

          {/* Gross Wages */}
          <div>
            <label
              htmlFor="original_gross_wages"
              className="block text-sm font-medium text-gray-700"
            >
              Gross Wages <span className="text-red-500">*</span>
            </label>
            <div className="mt-1 relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-gray-500 sm:text-sm">$</span>
              </div>
              <input
                type="number"
                id="original_gross_wages"
                step="0.01"
                {...register('original_gross_wages', {
                  required: 'Gross wages is required',
                  min: {
                    value: 0.01,
                    message: 'Must be greater than 0',
                  },
                  valueAsNumber: true,
                })}
                className="mt-1 block w-full pl-7 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="0.00"
              />
            </div>
            {errors.original_gross_wages && (
              <p className="mt-1 text-sm text-red-600">
                {errors.original_gross_wages.message}
              </p>
            )}
          </div>

          {/* Gratuity (Hospitality Only) */}
          {formatType === 'HOSPITALITY' && (
            <div>
              <label
                htmlFor="original_gratuity"
                className="block text-sm font-medium text-gray-700"
              >
                Gratuity (Optional)
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 sm:text-sm">$</span>
                </div>
                <input
                  type="number"
                  id="original_gratuity"
                  step="0.01"
                  {...register('original_gratuity', {
                    min: {
                      value: 0,
                      message: 'Cannot be negative',
                    },
                    valueAsNumber: true,
                  })}
                  className="mt-1 block w-full pl-7 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="0.00"
                />
              </div>
              {errors.original_gratuity && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.original_gratuity.message}
                </p>
              )}
            </div>
          )}

          {/* Contribution Preview */}
          {contributionPreview && (
            <div className="pt-4 border-t border-gray-200">
              <ContributionPreview preview={contributionPreview} />
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="mt-6 flex justify-end space-x-3 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            disabled={isLoading}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading
              ? 'Saving...'
              : initialData
                ? 'Update Record'
                : 'Add Record'}
          </button>
        </div>
      </form>
    </div>
  );
}
